import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { CommentStore, DeletionQueueCommentType } from '@/stores/CommentStore';
import { CommentType } from '@/types/Content/Comment';
import commentService from '@/services/commentService';

const CommentContext = createContext<CommentStore>(null);

type CommentContextPropTypes = {
  contentId: string;
  comments: Array<CommentType>;
  onComment?(comment: CommentType): void;
  onDelete?(comment: CommentType): void;
};

function initializeComments(comments: Array<CommentType>): Array<CommentType> {
  if (comments?.length) {
    return comments;
  }
  return [];
}

export const CommentsProvider = (
  props: PropsWithChildren<CommentContextPropTypes>,
): JSX.Element => {
  const {
    contentId: propId,
    comments: propComments,
    children,
    onComment,
    onDelete,
  } = props ?? {};
  const [contentId, setContentId] = useState<string>(propId);
  const [comments, setComments] = useState<CommentType[]>(
    initializeComments(propComments),
  );
  const [deletionQueue, setDeletionQueue] = useState<
    DeletionQueueCommentType[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  function cancelDeletion(): void {
    setDeletionQueue([]);
  }

  function clearErrors() {
    setErrors(new Set());
  }

  async function remove(
    comment: DeletionQueueCommentType,
  ): Promise<DeletionQueueCommentType> {
    let response;
    if (comment.parentId) {
      response = await commentService.removeReply(
        comment,
        contentId,
        comment.parentId,
      );
    } else {
      response = await commentService.remove(comment, contentId);
    }
    const { error } = response;

    if (error) {
      const newErrors = new Set(errors);
      newErrors.add(`Failed to delete comment ${comment?.id}`);
      setErrors(newErrors);
      return null;
    }

    const newComments = JSON.parse(
      JSON.stringify(comments),
    ) as Array<CommentType>;

    if (comment.parentId) {
      /* reply */
      const prevItemIndex = newComments.findIndex(
        (item) => item?.id === comment?.parentId,
      );
      const prevItem = newComments[prevItemIndex];
      if (prevItem) {
        newComments.splice(prevItemIndex, 1, {
          ...prevItem,
          replies:
            prevItem?.replies?.filter((el) => el.id !== comment.id) || null,
        });
      }
    } else {
      /* comment */
      const oldContentIndex = comments.findIndex(
        (oldComment) => oldComment?.id === comment?.id,
      );
      if (oldContentIndex > -1) newComments.splice(oldContentIndex, 1);
    }

    setComments(newComments);

    return comment;
  }

  function shiftDeletionQueue(): DeletionQueueCommentType {
    const queueCopy = JSON.parse(
      JSON.stringify(deletionQueue),
    ) as Array<DeletionQueueCommentType>;
    const commentToDelete = queueCopy.shift();
    setDeletionQueue(queueCopy);
    return commentToDelete;
  }

  async function confirmDeletion() {
    setIsDeleting(true);
    const commentToDelete = shiftDeletionQueue();
    const result = await remove(commentToDelete);
    onDelete?.(commentToDelete);
    setIsDeleting(false);

    return result;
  }

  function pushToDeletionQueue(comment: DeletionQueueCommentType): void {
    const newDeletionQueue = JSON.parse(
      JSON.stringify(deletionQueue),
    ) as Array<DeletionQueueCommentType>;
    newDeletionQueue.push(comment);
    setDeletionQueue(newDeletionQueue);
  }

  function pushCommentToList(comment) {
    const newComments = JSON.parse(
      JSON.stringify(comments),
    ) as Array<CommentType>;
    newComments.push(comment);
    setComments(newComments);
  }
  function pushReplyToList(reply, commentId) {
    const newComments = JSON.parse(
      JSON.stringify(comments),
    ) as Array<CommentType>;

    const prevItemIndex = newComments.findIndex(
      (item) => item?.id === commentId,
    );
    const prevItem = newComments[prevItemIndex];

    if (prevItem) {
      newComments.splice(prevItemIndex, 1, {
        ...prevItem,
        replies: [...(prevItem.replies || []), reply],
      });
    }
    setComments(newComments);
  }

  function populate(newContentId: string, newComments: Array<CommentType>) {
    setComments(newComments);
    setContentId(newContentId);
  }

  function clean() {
    setComments([]);
    setContentId('');
    setDeletionQueue([]);
    setErrors(new Set());
  }

  async function create(comment: Partial<CommentType>): Promise<CommentType> {
    setIsLoading(true);

    const commentParams = {
      content_id: contentId,
      user_id: comment.user.id,
      body: comment.body,
    };

    const { data } = await commentService.create(commentParams);

    if (data?.error) {
      const newErrors = new Set(errors);
      newErrors.add('Failed to create new comment');
      setErrors(newErrors);
      return null;
    }

    if (!data) {
      return null;
    }

    const newComment = {
      ...comment,
      id: data?._key,
      createdAt: new Date().toISOString(),
    } as CommentType;

    pushCommentToList(newComment);

    onComment?.(newComment);

    setIsLoading(false);

    return newComment;
  }

  async function createReply(
    reply: Partial<CommentType>,
    { parentId }: { parentId: string },
  ): Promise<CommentType> {
    setIsLoading(true);

    const { data } = await commentService.createReply(
      {
        text: reply.body,
      },
      {
        contentId,
        commentId: parentId,
      },
    );

    if (data?.error) {
      const newErrors = new Set(errors);
      newErrors.add('Failed to create new comment');
      setErrors(newErrors);
      return null;
    }

    if (!data) {
      return null;
    }

    const newReply = {
      ...reply,
      id: data._key,
      createdAt: new Date().toISOString(),
    } as CommentType;

    pushReplyToList(newReply, parentId);

    setIsLoading(false);

    return newReply;
  }

  const commentBag: CommentStore = {
    contentId,
    comments,
    isLoading,
    isDeleting,
    errors,
    deletionQueue,
    totalCount: comments?.length || 0,
    lastComment: comments?.[comments?.length - 1] || null,
    populate,
    clean,
    create,
    createReply,
    cancelDeletion,
    clearErrors,
    confirmDeletion,
    pushToDeletionQueue,
    shiftDeletionQueue,
    remove,
  };

  useEffect(() => {
    setContentId(propId);
    setComments(initializeComments(propComments));
  }, [propId, propComments]);

  return (
    <CommentContext.Provider value={commentBag}>
      {children}
    </CommentContext.Provider>
  );
};
export const useCommentStore = (): CommentStore => useContext(CommentContext);
